import { usePathStateStore } from '@client/stores/PathStateStore';
import { useLocation } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface UsePathStateOptions<T> {
    serialize?: (value: T | undefined) => string;
    deserialize?: (value: string) => T | undefined;
}

export type UsePathStateReturn<T> = [T, (value: T | ((prev: T) => T)) => void];

const STORAGE_PREFIX = 'pathState:';

/** The store key a `usePathState(key)` instance uses on a given pathname. */
export function getPathStateKey(pathname: string, key: string): string {
    return `${STORAGE_PREFIX}${pathname}:${key}`;
}

/**
 * Whether path state already exists for `(pathname, key)` — i.e. the page was visited earlier in
 * this session. Path state survives in-app navigation and full reloads within the tab
 * (session-scoped storage, CUR-615); it clears when the tab is closed. Used to detect a
 * "return visit" so callers don't clobber restored state.
 */
export function hasStoredPathState(pathname: string, key: string): boolean {
    return usePathStateStore.getState().getState(getPathStateKey(pathname, key)) !== undefined;
}

// Overload signatures to match useState behavior
export function usePathState<T = undefined>(
    key: string,
): [T | undefined, (value: T | undefined | ((prev: T | undefined) => T | undefined)) => void];

export function usePathState<T>(key: string, defaultValue: T | (() => T)): [T, (value: T | ((prev: T) => T)) => void];
export function usePathState<T>(
    key: string,
    defaultValue?: T | (() => T),
): [T | undefined, (value: T | undefined | ((prev: T | undefined) => T | undefined)) => void] {
    const location = useLocation();
    const pathname = location.pathname;

    // Track the pathname this hook instance was created with
    const mountedPathRef = useRef(pathname);
    const isMountedRef = useRef(true);

    // Create storage key based on the mounted pathname and key
    const storageKey = getPathStateKey(mountedPathRef.current, key);

    // Helper to resolve the default value (handle both direct values and functions)
    const resolveDefaultValue = (): T | undefined => {
        return typeof defaultValue === 'function' ? (defaultValue as () => T)() : defaultValue;
    };

    // Initialize state from Zustand store
    const [state, setState] = useState<T | undefined>(() => {
        const storedState = usePathStateStore.getState().getState(storageKey);
        // Only use stored state if it exists
        if (!storedState) {
            return resolveDefaultValue();
        }
        const resolvedDefault = resolveDefaultValue();
        return (storedState.value as T) ?? resolvedDefault;
    });

    // Save state to Zustand store whenever it changes
    useEffect(() => {
        if (isMountedRef.current && pathname === mountedPathRef.current) {
            const resolvedDefault = resolveDefaultValue();
            if (state !== undefined || resolvedDefault === undefined) {
                usePathStateStore.getState().setState(storageKey, state, mountedPathRef.current);
            } else {
                // Remove the key if state is undefined but defaultValue is not
                usePathStateStore.getState().deleteState(storageKey);
            }
        }
    }, [state, storageKey, pathname]);

    // Handle unmount
    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    // Subscribe to Zustand store changes for this specific key
    useEffect(() => {
        const unsubscribe = usePathStateStore.subscribe(
            (state) => state.states[storageKey],
            (stateValue) => {
                if (stateValue) {
                    setState((stateValue.value as T) ?? resolveDefaultValue());
                }
                // Deliberately NO local reset when the key is deleted. Deletion comes from
                // navigation cleanup (cleanupStatesForNavigation) while this page is being left:
                // resetting here re-renders the still-mounted page with the default value, and the
                // save effect above then writes that default straight back into the store before
                // the router commits the new location — resurrecting state the cleanup just wiped.
                // The next visit remounts and reads the store fresh, so no reset is needed.
            },
        );

        return unsubscribe;
    }, [storageKey]);

    // Custom setter that works like setState
    const setValue = useCallback((value: T | undefined | ((prev: T | undefined) => T | undefined)) => {
        setState((prev) => {
            const newValue =
                typeof value === 'function' ? (value as (prev: T | undefined) => T | undefined)(prev) : value;
            return newValue;
        });
    }, []);

    return [state, setValue];
}
